import React, {useEffect, useState, useCallback, useMemo} from 'react';
import {useLoadingStore} from "@/stores/loading";
import ConfirmModal from "@/components/ConfirmModal";
import {MESSAGE_NO_E39} from "@/config";
import {useNavigate} from 'react-router-dom';
import {axios} from "@/lib/axios";
import qs from 'qs';
import {useSearchParams} from 'react-router-dom';
import ReactPaginate from "react-paginate";
import {Bills, Fees, Logs} from "@/containers/drmPf/APIUsages/types";
import {useAPIUsagesInit} from "@/containers/drmPf/APIUsages/useAPIUsagesInit";
import {Spinner} from "@/components/Spinner";

export default function APIUsagesDetail() {
  // 日本時間（JST）を基準に YYYY-MM-DD 形式を取得
  const formatDate = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // 今月の初日を取得
  const getFirstDayOfMonthStr = () => {
    const now = new Date();
    return formatDate(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  // 今日を取得
  const getTodayStr = () => {
    return formatDate(new Date());
  };

  const {
    drm_pf_api_summary_th,
    drm_pf_api_summary_th_2nd,
    drm_pf_api_details_th,
    drm_pf_api_usages_th,
    pageSizeOptions
  } = useAPIUsagesInit();

  const setLoading = useLoadingStore(state => state.setLoading);
  const [logs, setLogs] = useState<Logs[]>([]);
  const [fees, setFees] = useState<Fees[]>([]);
  const [bills, setBills] = useState<Bills[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isShowErrorModal, setIsShowErrorModal] = useState(false);

  const navigate = useNavigate();
  // 検索項目
  const [fromDate, setFromDate] = useState<string>(getFirstDayOfMonthStr()); // 今月1日
  const [toDate, setToDate] = useState<string>(getTodayStr());               // 今日
  const [pageSize, setPageSize] = useState<string>("");
  const [page, setPage] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();

  const [billsOpen, setBillsOpen] = useState(true);
  const [open, setOpen] = useState(true);
  const [logsOpen, setLogsOpen] = useState(true);

  const onClickToggleBills = () => setBillsOpen((prev) => !prev);
  const onClickToggle = () => setOpen((prev) => !prev);
  const onClickToggleLogs = () => setLogsOpen((prev) => !prev);

  const [tableLoading, setTableLoading] = useState<{ log: boolean, fee: boolean, bill: boolean }>({
    log: false,
    fee: false,
    bill: false
  });

  //表示数変更時処理
  const onSelectedPageSize = (pageSize: string) => {
    const params = {
      //id: Number(customerId),
      from_date: fromDate,
      to_date: toDate,
      page_size: pageSize,
      page: page
    }
    fetchLogs(params);
  }

  //ページ数移動時処理
  const onPageChange = (page: string) => {
    const params = {
      //id: Number(customerId),
      from_date: fromDate,
      to_date: toDate,
      page_size: pageSize,
      page: page
    }
    fetchLogs(params);
  }

  useEffect(() => {
    // URLパラメータから検索条件を取得し、状態に反映する
    const params = qs.parse(searchParams.toString(), {ignoreQueryPrefix: true});
    //console.log(params);
    if (typeof params.from_date === 'string') {
      setFromDate(params.from_date);
    }
    if (typeof params.to_date === 'string') {
      setToDate(params.to_date);
    }
    if (typeof params.page_size === 'string') {
      setPageSize(params.page_size);
    }
    if (typeof params.page === 'string') {
      setPage(params.page);
    }

    const initialFrom = typeof params.from_date === 'string' ? params.from_date : fromDate;
    const initialTo = typeof params.to_date === 'string' ? params.to_date : toDate;

    // 初回表示時や日付がある場合に実行
    if (initialFrom && initialTo) {
      fetchBills(); 
    }   

  }, [searchParams]);


  // 検索結果
  const [paging, setPaging] = useState<any>(undefined);
  const forcePage = useMemo(() => (paging?.page ?? 1) - 1, [paging?.page]);
  const handlePageChange = useCallback((selectedItem: { selected: number }) => {
    onPageChange(String(selectedItem.selected + 1));
  }, [onPageChange]);
  const [selectedPageSize, setSelectedPageSize] = useState<string | ''>('100')

  // 検索処理
  // 請求データ
  function fetchBills() {
    const params = {
      //id: Number(customerId),
      from_date: fromDate,
      to_date: toDate,
      page_size: pageSize,
      page: page
    }

    setTableLoading(p => ({...p, bill: true}));
    axios.get('/api/v1/customers/getMypageDrmPfApiUsageBills', {
      params: params
    }).then(result => {
      setBills(result.data.billData);
    }).catch(error => {
      setErrorMessage(MESSAGE_NO_E39);
    }).finally(() => {
      setTableLoading(p => ({...p, bill: false}));
    });
  }

  // API内訳
  function fetchFees() {
    const params = {
      from_date: fromDate,
      to_date: toDate,
      page_size: pageSize,
      page: page
    }
    setTableLoading(p => ({...p, fee: true}));
    axios.get('/api/v1/customers/getMypageDrmPfapiGroups', {
      params: params
    }).then(result => {
      setFees(result.data.apiGroup);
    }).catch(error => {
      setErrorMessage(MESSAGE_NO_E39);
    }).finally(() => {
      setTableLoading(p => ({...p, fee: false}));
    });
  }

  // APIログ
  function fetchLogs(params: any) {
    setTableLoading(p => ({...p, log: true}));
    axios.get('/api/v1/customers/getMypageDrmPfApiLogs', {
      params: params
    }).then(result => {
      setSearchParams(qs.stringify(result.data.params));
      setLogs(result.data.displayLogData);
      setFromDate(result.data.from_date);
      setToDate(result.data.to_date);
      setPaging(result.data.paging);
      if (typeof params.page_size === 'string') {
        setSelectedPageSize(params.page_size);
      }
    }).catch(error => {
      setErrorMessage(MESSAGE_NO_E39);
    }).finally(() => {
      setTableLoading(p => ({...p, log: false}));
    });
  }

  const onSearch = () => {
    const params = {
      //id: Number(customerId),
      from_date: fromDate,
      to_date: toDate,
      page_size: pageSize,
      page: page
    }
    fetchBills();
    fetchLogs(params);
    fetchFees();
  }

  const onSearchLogs = () => {
    const params = {
      //id: Number(customerId),
      from_date: fromDate,
      to_date: toDate,
      page_size: pageSize ? pageSize : 100,
      page: page ? page : 1
    }
    fetchLogs(params);
  }

  return (
    <div>
      <div className="info-tbl_wrap uk-margin-small-top">
        <table className="API-table uk-width-1-1">
          <tbody>
          <tr>
            <th className="label uk-text-center uk-background-muted">照会年月日</th>
            <td className="content uk-flex uk-flex-between">
              <div>
                <div className="uk-inline">
                  <input
                    name="to_date"
                    type="date"
                    className="uk-width-1-1"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div className="uk-inline">
                  ～
                </div>
                <div className="uk-inline">
                  <input
                    name="from_date"
                    type="date"
                    className="uk-width-1-1"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
                <div className="uk-inline uk-margin-large-left">
                  <button type="button" className="uk-button--m uk-button-refer" onClick={() => onSearch()}>全検索
                  </button>
                </div>
              </div>
            </td>
          </tr>
          </tbody>
        </table>

        <div className="uk-width-1-1 uk-margin-top uk-overflow-auto uk-position-relative">
          {tableLoading.bill && <Spinner/>}
          <button type="button"
                className="uk-button uk-button-text uk-text-primary"
                onClick={onClickToggleBills}
            >
            {billsOpen ? "▲" : "▼"} API請求情報 一覧
          </button>
          <div className={`toggle-contents uk-margin-small-top uk-overflow-auto ${billsOpen ? "visible" : "hidden"}`}>
            <div className="uk-flex uk-flex-right uk-margin-small-bottom">
              <button type="button" className="uk-button--l uk-button-refer" onClick={() => fetchBills()}>API請求情報 検索</button>
            </div>
            <table className="API-table">
              <thead>
              <tr>
                {drm_pf_api_summary_th.map((item, index) => {
                  const replaced_label = item.label.split(/(\n)/).map((item, index) => {
                    return (
                      <React.Fragment key={index}>
                        {item.match(/\n/) ? <br/> : item}
                      </React.Fragment>
                    );
                  });
                  const style: React.CSSProperties = {
                    width: item.width
                  };
                  return (
                    <th className={`label uk-text-center ${item.bold && "uk-text-bold"}`}
                        style={style}
                        rowSpan={item.rowspan}
                        colSpan={item.colspan}
                        key={index}
                    >
                      {replaced_label}
                    </th>
                  );
                })}
                </tr>
                <tr>
              {drm_pf_api_summary_th_2nd.map((item, index) => {
                const style: React.CSSProperties = {
                  width: item.width
                };
                return (
                  <th className="label uk-text-center" style={style} key={index}>{item.label}</th>
                );
              })}
              </tr>
              </thead>
              <tbody>
              {Object.values(bills).length !== 0 && Object.values(bills).map((item, index) => (
                <tr key={index}>
                  <td className="uk-text-center">{item.paymentType}</td>
                  <td className="uk-text-right">￥ {item.basicFee}</td>
                  <td className="uk-text-right">{item.validTotalAll} 件</td>
                  <td className="uk-text-right">￥ {item.undiscountPriceTotal}</td>
                  <td className="uk-text-right">￥ {item.discountAmount}</td>
                  <td className="uk-text-right">{item.realDiscountRate} %</td>
                  <td className="uk-text-right">￥ {item.discountPriceTotal}</td>
                  <td className="uk-text-right">{item.rate} %</td>
                  <td className="uk-text-right">{item.termDiscounts} %</td>
                  <td className="uk-text-right">￥ {item.allDiscountPriceTotal}</td>
                  <td className="uk-text-right uk-text-bold">￥ {item.billingAmount}</td>
                  <td className="uk-text-right uk-text-bold">{item.tax} %</td>
                  <td className="uk-text-right uk-text-bold">￥ {item.includeTax}</td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="uk-margin-small-top toggle-wrapper uk-position-relative">
          {tableLoading.fee && open && <Spinner/>}
          <button type="button"
                  className="uk-button uk-button-text uk-text-primary"
                  onClick={onClickToggle}
          >
            {open ? "▲" : "▼"} API利用料 内訳
          </button>
          <div className={`toggle-contents uk-margin-small-top uk-overflow-auto ${open ? "visible" : "hidden"}`}>
            <div className="uk-flex uk-flex-right uk-margin-small-bottom">
              <button type="button" className="uk-button--l uk-button-refer" onClick={() => fetchFees()}>API利用料
                検索
              </button>
            </div>
            <table className="API-table uk-width-auto">
              <thead>
              <tr>
                {drm_pf_api_details_th.map((item, index) => {
                  const style: React.CSSProperties = {
                    width: item.width
                  };
                  return (
                    <th className="label uk-text-center" style={style} key={index}>{item.label}</th>
                  );
                })}
              </tr>
              </thead>
              <tbody>
              {Object.values(fees).length !== 0 && Object.values(fees).map((item, index) => (

                <tr key={item.api_id}>
                  <td>{item.api_id}</td>
                  <td className="uk-text-right">{item.total} 件</td>
                  <td className="uk-text-right">{item.temporary_free_total} 件</td>
                  <td className="uk-text-right">{item.valid_total} 件</td>
                  <td className="uk-text-right">￥ {item.unit_price}</td>
                  <td className="uk-text-right">￥ {item.subtotal}</td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="uk-width-1-1 uk-margin-top uk-margin-bottom uk-overflow-auto uk-position-relative">
          {tableLoading.log && logsOpen && <Spinner/>}
          <button type="button"
                  className="uk-button uk-button-text uk-text-primary"
                  onClick={() => setLogsOpen((prev) => !prev)}
          >
            {logsOpen ? "▲" : "▼"} APIログ 一覧
          </button>
          <div className={`toggle-contents uk-margin-small-top uk-overflow-auto ${logsOpen ? "visible" : "hidden"}`}>
            <div className="uk-flex uk-flex-right">
              <button type="button" className="uk-button--l uk-button-refer" onClick={() => onSearchLogs()}>APIログ 検索
              </button>
            </div>
            <div className="uk-flex-between uk-flex uk-flex-middle">
              <div className="uk-inline">
                <select className="table-length" defaultValue={selectedPageSize}
                        onChange={(e) => onSelectedPageSize(e.target.value)}>
                  <option value="" disabled>表示件数</option>
                  {pageSizeOptions.map((o: any, index) => {
                    return <option key={`customers-list-page-size-${index}`} value={o.value}>{o.label}</option>
                  })}
                </select>
              </div>
              <p className="uk-text-right uk-margin-remove-bottom">
                ※こちらの小計はボリュームディスカウント前の金額となります。
              </p>
            </div>
            <div className="uk-width-1-1 uk-overflow-auto uk-margin-bottom uk-position-relative">
              <table className="API-table">
                <thead>
                <tr>
                  {drm_pf_api_usages_th.map((item, index) => {
                    const style: React.CSSProperties = {
                      width: item.width
                    };
                    return (
                      <th className="label uk-text-center" style={style} key={index}>{item.label}</th>
                    );
                  })}
                </tr>
                </thead>
                <tbody>
                {logs.length !== 0 && logs.map((item, index) => (
                  <tr key={index}>
                    <td>{item.start_date}</td>
                    <td>{item.end_date}</td>
                    <td>{item.ip_address}</td>
                    <td>{item.api_id}</td>
                    <td className="uk-text-center">{item.processed_result}</td>
                    <td className="uk-text-center">{item.processed_kind}</td>
                    <td className="uk-text-center">{item.edition_information}版</td>
                    <td className="uk-text-right">{item.data_number} 件</td>
                    <td className="uk-text-right">￥ {item.unit_price}</td>
                    <td className="uk-text-center">{item.is_billing_str}</td>
                    {item.is_temporary_free === 1 ? <td className="uk-text-right"> -{item.data_number} 件</td> :
                      <td className="uk-text-right"></td>}
                    <td className="uk-text-right">￥ {item.subtotal}</td>
                  </tr>
                ))}

                </tbody>
              </table>

              <div className="uk-text-center mt-5">
                全{paging?.count}件（{paging?.page}/{paging?.pageCount}）
              </div>
              <div className="pagination-container">
                <a onClick={() => {
                  onPageChange('1')
                }}>FIRST</a>
                <ReactPaginate
                  forcePage={forcePage} // ReactPaginate→0始まり CakePHP→1始まり
                  onPageChange={handlePageChange}
                  pageRangeDisplayed={5}
                  pageCount={paging?.pageCount}
                  renderOnZeroPageCount={null}
                  containerClassName='pagination'
                  activeLinkClassName="uk-text-emphasis"
                  previousLabel='<'
                  nextLabel='>'
                  breakLabel='...'
                />
                <a onClick={() => {
                  onPageChange(String(paging?.pageCount))
                }}>LAST</a>
              </div>
            </div>
          </div>
        </div>

        {errorMessage &&
            <ConfirmModal
                text={errorMessage}
                confirmButtonText="OK"
                isShow={isShowErrorModal}
                onConfirm={() => {
                  setIsShowErrorModal(false);
                  setErrorMessage(null);
                  navigate("/drm_pf/info");
                }}
            />
        }
      </div>
    </div>
  );
}
